import React, { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Md from "react-icons/md";
import * as Fi from "react-icons/fi";
import * as Hi from "react-icons/hi";
import Utils from "../../utils/Utils";
import EnquiriesTableSkelton from "./EnquiriesTableSkelton";
import { getStatusColour } from "../../constants/ReservationStatus";
import TablePagination, {
  DEFAULT_PAGE_SIZE,
  paginateList,
} from "./TablePagination";

function splitBooking(bookingTime) {
  if (!bookingTime) return { date: null, time: null };
  const [date, time] = String(bookingTime).split(" ");
  return { date, time };
}

function formatBookingDate(dateStr) {
  if (!dateStr) return "N/A";
  const d = new Date(
    /^\d{4}-\d{2}-\d{2}$/.test(dateStr) ? `${dateStr}T00:00:00` : dateStr
  );
  if (Number.isNaN(d.getTime())) return Utils.formatDate(dateStr) || "N/A";
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function EnquiryListPanel({
  items,
  loading = false,
  title = "Reservations",
  emptyMessage = "No reservations",
  showStatus = true,
  pageSize = DEFAULT_PAGE_SIZE,
}) {
  const navigate = useNavigate();
  const [expandedId, setExpandedId] = useState(null);
  const [page, setPage] = useState(1);
  const list = Array.isArray(items) ? items : [];

  useEffect(() => {
    setPage(1);
    setExpandedId(null);
  }, [items]);

  const {
    items: pageItems,
    page: safePage,
    total,
  } = paginateList(list, page, pageSize);

  const toggleExpand = (id, e) => {
    e?.stopPropagation?.();
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const openReservation = (enquiry) => {
    if (!enquiry?.id) return;
    navigate(`/view-reservation/${enquiry.id}`);
  };

  return (
    <div className="el-panel">
      {/* Desktop table */}
      <div className="el-table-wrap">
        <table className="el-table">
          <thead>
            <tr>
              <th>Guest</th>
              <th>Reservation ID</th>
              <th>Booking date</th>
              <th>Time</th>
              <th>Party size</th>
              {showStatus && <th>Status</th>}
              <th className="el-table__details-col">Details</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <EnquiriesTableSkelton cols={showStatus ? 7 : 6} />
            ) : list.length === 0 ? (
              <tr>
                <td colSpan={showStatus ? 7 : 6} className="el-table__empty">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              pageItems.map((enquiry) => {
                const { date, time } = splitBooking(enquiry?.bookingTime);
                const id = enquiry?.id ?? enquiry?.formattedID;
                const isOpen = expandedId === id;
                const statusColour = getStatusColour(enquiry?.status);

                return (
                  <Fragment key={id}>
                    <tr
                      className={`el-table__row${isOpen ? " is-open" : ""}`}
                      onClick={() => openReservation(enquiry)}
                    >
                      <td className="el-table__guest">
                        {enquiry?.name ?? "N/A"}
                      </td>
                      <td className="el-table__id">
                        {enquiry?.formattedID ?? "N/A"}
                      </td>
                      <td>{formatBookingDate(date)}</td>
                      <td>
                        {time != null ? Utils.convertTiming(time) : "N/A"}
                      </td>
                      <td>
                        <span className="el-party">
                          <Fi.FiUser aria-hidden />
                          {enquiry?.chairs ?? "N/A"}
                        </span>
                      </td>
                      {showStatus && (
                        <td>
                          <span
                            className="el-status"
                            style={{
                              backgroundColor: statusColour.text,
                              color: statusColour.bg,
                              borderColor: statusColour.text,
                            }}
                          >
                            {enquiry?.status ?? "N/A"}
                          </span>
                        </td>
                      )}
                      <td className="el-table__details-col">
                        <button
                          type="button"
                          className="el-expand-btn"
                          aria-expanded={isOpen}
                          aria-label={isOpen ? "Hide details" : "Show details"}
                          onClick={(e) => toggleExpand(id, e)}
                        >
                          {isOpen ? (
                            <Hi.HiChevronUp />
                          ) : (
                            <Hi.HiChevronDown />
                          )}
                        </button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="el-table__detail-row">
                        <td colSpan={showStatus ? 7 : 6}>
                          <div className="el-detail">
                            <div className="el-detail__item">
                              <Fi.FiMail aria-hidden />
                              <span className="el-detail__label">Email</span>
                              <span className="el-detail__value">
                                {enquiry?.email || "N/A"}
                              </span>
                            </div>
                            <div className="el-detail__divider" aria-hidden />
                            <div className="el-detail__item">
                              <Fi.FiPhone aria-hidden />
                              <span className="el-detail__label">Phone</span>
                              <span className="el-detail__value">
                                {enquiry?.phone || "N/A"}
                              </span>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="el-cards" aria-label={title}>
        {loading ? (
          <div className="el-cards__loading">Loading…</div>
        ) : list.length === 0 ? (
          <div className="el-cards__empty">{emptyMessage}</div>
        ) : (
          pageItems.map((enquiry) => {
            const { date, time } = splitBooking(enquiry?.bookingTime);
            const id = enquiry?.id ?? enquiry?.formattedID;
            const isOpen = expandedId === id;
            const statusColour = getStatusColour(enquiry?.status);

            return (
              <article
                key={id}
                className={`el-card${isOpen ? " is-open" : ""}`}
              >
                <div
                  className="el-card__main"
                  onClick={() => openReservation(enquiry)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openReservation(enquiry);
                    }
                  }}
                >
                  <div className="el-card__top">
                    <div className="el-card__identity">
                      <div className="el-card__name">
                        {enquiry?.name ?? "N/A"}
                      </div>
                      <div className="el-card__id">
                        {enquiry?.formattedID ?? "N/A"}
                      </div>
                    </div>
                    <div className="el-card__meta-right">
                      <span className="el-party">
                        <Fi.FiUser aria-hidden />
                        {enquiry?.chairs ?? "N/A"}
                      </span>
                      <button
                        type="button"
                        className="el-expand-btn"
                        aria-expanded={isOpen}
                        aria-label={isOpen ? "Hide details" : "Show details"}
                        onClick={(e) => toggleExpand(id, e)}
                      >
                        {isOpen ? <Hi.HiChevronUp /> : <Hi.HiChevronDown />}
                      </button>
                    </div>
                  </div>

                  <div className="el-card__when">
                    <span className="el-card__when-item">
                      <Md.MdOutlineCalendarToday aria-hidden />
                      {formatBookingDate(date)}
                    </span>
                    <span className="el-card__sep" aria-hidden>
                      |
                    </span>
                    <span className="el-card__when-item">
                      <Md.MdOutlineAccessTime aria-hidden />
                      {time != null ? Utils.convertTiming(time) : "N/A"}
                    </span>
                  </div>

                  {showStatus && enquiry?.status && (
                    <span
                      className="el-status el-status--card"
                      style={{
                        backgroundColor: statusColour.text,
                        color: statusColour.bg,
                        borderColor: statusColour.text,
                      }}
                    >
                      {enquiry.status}
                    </span>
                  )}
                </div>

                {isOpen && (
                  <div className="el-card__detail">
                    <div className="el-detail el-detail--stack">
                      <div className="el-detail__item">
                        <Fi.FiMail aria-hidden />
                        <span className="el-detail__label">Email</span>
                        <span className="el-detail__value">
                          {enquiry?.email || "N/A"}
                        </span>
                      </div>
                      <div className="el-detail__item">
                        <Fi.FiPhone aria-hidden />
                        <span className="el-detail__label">Phone</span>
                        <span className="el-detail__value">
                          {enquiry?.phone || "N/A"}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </article>
            );
          })
        )}
      </div>

      {!loading && total > 0 && (
        <TablePagination
          page={safePage}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
        />
      )}
    </div>
  );
}

export default EnquiryListPanel;
